import Decimal from 'decimal.js';
import { RotationCallStatus } from 'src/types/ViewModel/Stowage/Rotation/RotationCallStatus';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { notStowedWeightInTanks, stowedWeightInTanks } from './stowageOperationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { UUID } from 'crypto';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { isPortCallAfterFirstVoyageOrcaWebStowageImosSync } from '../stowageRotationUtils';
import { calculateUnloadingDifference } from './operationDisplayUtil';
import { hasAnyEditedContractWeight } from '../cargoOperationUtils';
import { STOWAGE_INPUT_TOLERANCE } from '../stowagePrecision';

export const calculateRotationCallStatuses = (stowagePlan: StowagePlan, firstVoyageOrcaWebStowageImosSync: number): RotationCallStatus[] => {
    if (!stowagePlan.berthCalls) {
        return [];
    }

    return (
        (stowagePlan.rotation ?? [])
            .filter((pc) => isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, firstVoyageOrcaWebStowageImosSync))
            .flatMap((pc) =>
                pc.berthCalls?.flatMap((bc) => {
                    return {
                        portCallGuid: bc.portCall.portCallGuid,
                        berthCallGuid: bc.berthCallGuid,
                        numberOfCargoOperations: bc.cargoOperations.length,
                        numberOfCargoOperationsWithMissingStowage: getNumberOfIncompleteCargoOperations(stowagePlan, bc),
                        hasContractWeightOverride: hasAnyEditedContractWeight(bc.cargoOperations ?? []),
                    } as RotationCallStatus;
                })
            ) ?? []
    );
};

export const getRotationCallStatusInBerthCall = (rotationCallStatuses: RotationCallStatus[], berthCallGuid: UUID): RotationCallStatus | undefined => {
    return rotationCallStatuses?.find((status) => status.berthCallGuid === berthCallGuid);
};

export const getRotationCallStatusInPortCall = (rotationCallStatuses: RotationCallStatus[], portCallGuid: UUID): RotationCallStatus => {
    return rotationCallStatuses
        ?.filter((status) => status.portCallGuid === portCallGuid)
        ?.reduce(
            (acc, status) => {
                acc.numberOfCargoOperations += status.numberOfCargoOperations;
                acc.numberOfCargoOperationsWithMissingStowage += status.numberOfCargoOperationsWithMissingStowage;
                acc.hasContractWeightOverride = acc.hasContractWeightOverride || status.hasContractWeightOverride;
                return acc;
            },
            {
                portCallGuid,
                berthCallGuid: null,
                numberOfCargoOperations: 0,
                numberOfCargoOperationsWithMissingStowage: 0,
                hasContractWeightOverride: false,
            }
        );
};

export const hasZeroStowedWeight = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): boolean => {
    const stowedWeight = stowedWeightInTanks(stowagePlan, cargoOperation).toDecimalPlaces(3, Decimal.ROUND_DOWN).abs();
    return stowedWeight.eq(0);
};

export const getNumberOfIncompleteCargoOperations = (stowagePlan: StowagePlan, berthCallItem: RotationBerthCall): number => {
    if (stowagePlan?.rotation && berthCallItem) {
        const numberOfOperations = berthCallItem.cargoOperations.reduce((count, op) => {
            // Skip Transit operations - they should not be counted for missing stowage
            if (op.operationType === 'Transit') {
                return count;
            }

            // Check for zero stowed weight (missing stowage)
            if (hasZeroStowedWeight(stowagePlan, op)) {
                return count + 1;
            }

            // Check for invalid stowage in loading operations (negative not-stowed weight beyond tolerance)
            if (op.operationType === 'Loading' && notStowedWeightInTanks(stowagePlan, op).lt(STOWAGE_INPUT_TOLERANCE.negated())) {
                return count + 1;
            }

            // Check for unloading operations with remaining cargo to unload (difference between stowed and unstowed)
            if (op.operationType === 'Unloading') {
                const difference = calculateUnloadingDifference(stowagePlan, op);
                if (difference.gt(0)) {
                    return count + 1;
                }
            }

            return count;
        }, 0);
        return numberOfOperations;
    }
    return 0;
};

export const getNumberOfIncompleteStowages = (rotationCallStatuses: RotationCallStatus[]): number => {
    return rotationCallStatuses?.reduce((acc, status) => acc + status.numberOfCargoOperationsWithMissingStowage, 0);
};

export const getValueOrMax = (value?: number): string | undefined => {
    if (value > 99) {
        return '99+';
    }
    return value?.toString();
};
